import { useMemo } from 'react';
import { create } from 'zustand';
import type { PopulationBlob } from '../lib/blobLoader';
import { getAttribute, type AttributeKind } from '../lib/attributes';
import { useBlobStore } from './blobStore';
import { useSelectionStore } from './selectionStore';

export type CategoricalOperator = 'in' | 'not-in';
export type OrdinalOperator = 'in' | 'range';
export type BooleanOperator = 'equals';

export type CategoricalStage = {
  id: string;
  kind: 'categorical';
  attributeKey: string;
  operator: CategoricalOperator;
  value: number[];
};

export type OrdinalStage = {
  id: string;
  kind: 'ordinal';
  attributeKey: string;
  operator: OrdinalOperator;
  value: number[] | [number, number];
};

export type BooleanStage = {
  id: string;
  kind: 'boolean';
  attributeKey: string;
  operator: BooleanOperator;
  value: 0 | 1;
};

export type FilterStage = CategoricalStage | OrdinalStage | BooleanStage;

type FilterState = {
  stages: FilterStage[];
  addStage: (stage: FilterStage) => void;
  updateStage: (id: string, patch: Partial<Omit<FilterStage, 'id' | 'kind' | 'attributeKey'>>) => void;
  removeStage: (id: string) => void;
  clearStages: () => void;
};

export const useFilterStore = create<FilterState>((set) => ({
  stages: [],
  addStage: (stage) => set((s) => ({ stages: [...s.stages, stage] })),
  updateStage: (id, patch) =>
    set((s) => ({
      stages: s.stages.map((stage) =>
        stage.id === id ? ({ ...stage, ...patch } as FilterStage) : stage,
      ),
    })),
  removeStage: (id) =>
    set((s) => ({ stages: s.stages.filter((stage) => stage.id !== id) })),
  clearStages: () => set({ stages: [] }),
}));

export function applyStage(
  stage: FilterStage,
  blob: PopulationBlob,
  indices: Uint32Array,
): Uint32Array {
  const attr = getAttribute(stage.attributeKey);
  if (!attr) return indices;
  const out = new Uint32Array(indices.length);
  let count = 0;

  if (stage.kind === 'categorical') {
    const set = new Set(stage.value);
    const matchIfIn = stage.operator === 'in';
    for (let k = 0; k < indices.length; k++) {
      const i = indices[k];
      const has = set.has(attr.accessor(blob, i));
      if (matchIfIn ? has : !has) out[count++] = i;
    }
  } else if (stage.kind === 'ordinal') {
    if (stage.operator === 'range') {
      const [min, max] = stage.value as [number, number];
      for (let k = 0; k < indices.length; k++) {
        const i = indices[k];
        const v = attr.accessor(blob, i);
        if (v >= min && v <= max) out[count++] = i;
      }
    } else {
      const set = new Set(stage.value as number[]);
      for (let k = 0; k < indices.length; k++) {
        const i = indices[k];
        if (set.has(attr.accessor(blob, i))) out[count++] = i;
      }
    }
  } else {
    const target = stage.value;
    for (let k = 0; k < indices.length; k++) {
      const i = indices[k];
      if (attr.accessor(blob, i) === target) out[count++] = i;
    }
  }

  return out.slice(0, count);
}

export function allIndices(n: number): Uint32Array {
  const out = new Uint32Array(n);
  for (let i = 0; i < n; i++) out[i] = i;
  return out;
}

export type FilteredResult = {
  indices: Uint32Array;
  baseCount: number;
  stageCounts: number[];
};

export function useFilteredIndices(): FilteredResult | null {
  const blob = useBlobStore((s) => s.blob);
  const baseSelection = useSelectionStore((s) => s.baseSelection);
  const stages = useFilterStore((s) => s.stages);

  return useMemo(() => {
    if (!blob) return null;
    const start = baseSelection ?? allIndices(blob.data.length);
    const stageCounts: number[] = [];
    let current = start;
    for (const stage of stages) {
      current = applyStage(stage, blob, current);
      stageCounts.push(current.length);
    }
    return {
      indices: current,
      baseCount: start.length,
      stageCounts,
    };
  }, [blob, baseSelection, stages]);
}

export function defaultStageFor(
  attributeKey: string,
  kind: AttributeKind,
  values: number[],
): FilterStage {
  const id = `stage-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  if (kind === 'categorical') {
    return {
      id,
      kind: 'categorical',
      attributeKey,
      operator: 'in',
      value: values.length > 0 ? [values[0]] : [],
    };
  }
  if (kind === 'ordinal') {
    const min = values.length > 0 ? values[0] : 0;
    const max = values.length > 0 ? values[values.length - 1] : 0;
    return {
      id,
      kind: 'ordinal',
      attributeKey,
      operator: 'range',
      value: [min, max],
    };
  }
  return {
    id,
    kind: 'boolean',
    attributeKey,
    operator: 'equals',
    value: 0,
  };
}
